/**
 * Byte-to-lowercase-hex encoding -- the `padStart(2, '0')` nibble contract.
 *
 * Three live encoders had each re-decided it independently (`utils/crypto/hashing.ts`, `utils/crypto/uuid.ts`,
 * `utils/stripe/idempotency.ts`), and the first two sit in the SAME directory yet cannot import each other:
 * `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` bans utils->utils value imports with no allowlist and does not exempt an
 * intra-directory `./sibling`. That is why the copies existed, and const/ is the one shared home the layer floor
 * permits. Same const-leaf-pinned shape as `const/signed-token-codec.ts`.
 *
 * The pad is the whole point: dropping it silently shortens a digest or a UUID whenever a byte is under 0x10, which
 * makes a hash collide with a DIFFERENT input's digest rather than throwing anywhere.
 *
 * Paired forbid-rule: `QUAL-INLINE-BYTE-HEX-PAD-001`.
 */

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): ONE byte as exactly two lowercase hex digits. Exported alongside
 * {@link bytesToHex} because `generateUuid` needs the PER-BYTE array (it slices the hex groups apart to place the UUID
 * dashes) and would otherwise re-roll the nibble contract to get it.
 */
export function byteToHex(byte: number): string {
	return byte.toString(16).padStart(2, '0');
}

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): a byte sequence as a contiguous lowercase hex string -- the digest
 * spelling every SHA-256 consumer here expects. Takes a `Uint8Array` rather than an `ArrayBuffer` so the caller owns the
 * view/copy decision (a view may sit on a SharedArrayBuffer, which `crypto.subtle` will not accept).
 */
export function bytesToHex(bytes: Uint8Array): string {
	return Array.from(bytes, byteToHex).join('');
}
